import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { duplicationSelectionEligibility } from "@/lib/ads-manager-duplication";
import { clearPendingAdmission, usePendingDuplicationAdmission } from "@/lib/ads-manager-duplication-admission";
import { apiRequest } from "@/lib/queryClient";
import { duplicationErrorMessage } from "@/lib/ads-manager-duplication";
import type { DuplicateAdsPreflightResponse } from "@shared/ads-manager-duplication";
import { DuplicateAdsDialog } from "./duplicate-ads-dialog";
import { AdsManagerLauncherAction } from "./launcher-draft-action";

type Row = { id: string; name: string; level: string; accountId?: string; metrics?: { spend?: number | null } };
type Props = {
  brandId: string; accountId: string; currency: string | null; activeLevel: string;
  selectedIds: string[]; rows: Row[]; ready: boolean;
  onSelectionChange: (ids: string[]) => void;
  buttonLabel?: string;
  buttonSize?: "sm" | "default";
  onAdmitted?: (id: string) => void;
  range?: { since: string; until: string };
  recoveryOnly?: boolean;
};

export function DuplicateAdsAction(props: Props) {
  const admission = usePendingDuplicationAdmission();
  const scope = JSON.stringify([props.brandId, props.accountId]);
  const [confirmed, setConfirmed] = useState<{ scope: string; id: string } | null>(null);
  // Do not discard an earlier, possibly admitted Meta run when changing modes.
  const blockedReason = admission.error ?? (admission.pending
    ? "A prior duplication admission needs confirmation. No replacement import can start until it is resolved." : null);
  return <>
    <AdsManagerLauncherAction {...props} actorId={admission.actorId} blockedReason={blockedReason} />
    {admission.pending && <ProofPreservingDuplicateAdsAction key={admission.actorId} {...props} recoveryOnly onAdmitted={id => {
      if (props.onAdmitted) props.onAdmitted(id);
      else setConfirmed({ scope, id });
    }} />}
    {confirmed?.scope === scope && <Alert data-testid="duplication-started">
      <AlertTitle>Prior admission confirmed</AlertTitle>
      <AlertDescription>Run ID: {confirmed.id}. Review the existing run in Ads Manager; no replacement import was started.</AlertDescription>
    </Alert>}
    {admission.error && <Alert role="alert"><AlertTitle>Duplication recovery unavailable</AlertTitle>
      <AlertDescription>{admission.error} No saved admission has been discarded.</AlertDescription></Alert>}
  </>;
}

export function ProofPreservingDuplicateAdsAction(props: Props) {
  const { brandId, accountId, selectedIds, rows, activeLevel, ready, currency, onSelectionChange } = props;
  const scope = JSON.stringify([brandId, accountId]);
  const selectionScope = JSON.stringify([scope, selectedIds]);
  const [openScope, setOpenScope] = useState<string | null>(null);
  const [started, setStarted] = useState<{ scope: string; id: string } | null>(null);
  const admission = usePendingDuplicationAdmission();
  const [recovering, setRecovering] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const recoveryGuard = useRef(false);
  const alive = useRef(true);
  const latestScope = useRef(scope);
  latestScope.current = scope;
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => { setRecovering(false); setRecoveryError(null); }, [scope]);
  const capability = useQuery<{ enabled: boolean; maxAds: number }>({
    queryKey: ["/api/brands", brandId, "ads-manager", "duplicate-ads", "capabilities", accountId, admission.actorId],
    retry: false, staleTime: 0, refetchOnMount: "always", refetchOnWindowFocus: "always",
    queryFn: async ({ signal }) => {
      const response = await fetch(`/api/brands/${brandId}/ads-manager/duplicate-ads/capabilities`, { credentials: "include", cache: "no-store", signal });
      if (!response.ok) throw new Error("Duplication capability is unavailable.");
      return response.json();
    },
  });
  const enabled = capability.data?.enabled === true;
  const brand = useQuery<{ name: string }>({
    queryKey: ["/api/brands", brandId, "duplication-name"],
    enabled: enabled && openScope === selectionScope,
    queryFn: async ({ signal }) => {
      const response = await fetch(`/api/brands/${brandId}`, { credentials: "include", signal });
      if (!response.ok) throw new Error("Brand name is unavailable.");
      const result = await response.json();
      if (typeof result.name !== "string" || !result.name.trim()) throw new Error("Brand name is unavailable.");
      return result;
    },
    retry: false,
  });
  const eligibility = duplicationSelectionEligibility(accountId, selectedIds, rows, Math.min(capability.data?.maxAds ?? 25, 25));
  const ads = selectedIds.map(id => rows.find(row => row.id === id)!).filter(Boolean);
  const visible = enabled && activeLevel === "ad" && eligibility.adOnly;
  const reason = eligibility.reason ?? admission.error ?? (admission.pending ? "Confirm the prior admission before starting another duplication." : null)
    ?? (!ready ? "Wait for the current account selection to load." : null);
  const pendingMatchesSelection = admission.pending?.brandId === brandId && admission.pending.payload.accountId === accountId
    && JSON.stringify(admission.pending.payload.adIds) === JSON.stringify(selectedIds);
  const dialogVisible = visible && ready && !eligibility.reason && openScope === selectionScope
    && (!admission.pending || pendingMatchesSelection);
  async function confirmPriorAdmission() {
    const pending = admission.pending;
    if (!pending || !enabled || capability.isError || capability.isFetching || !ready || recoveryGuard.current || pending.brandId !== brandId || pending.payload.accountId !== accountId) return;
    recoveryGuard.current = true;
    setRecovering(true); setRecoveryError(null);
    try {
      const check = await apiRequest("POST", `/api/brands/${brandId}/ads-manager/duplicate-ads/preflight`,
        { accountId, adIds: pending.payload.adIds });
      const preflight = await check.json() as DuplicateAdsPreflightResponse;
      if (preflight.accountId !== accountId || preflight.ads.length !== pending.payload.adIds.length
        || pending.payload.adIds.some(id => preflight.ads.filter(ad => ad.adId === id).length !== 1)
        || currency !== pending.currency || preflight.ads.some(ad => ad.source.currency !== pending.currency))
        throw new Error("Fresh source currency or identity differs from the saved admission. Do not replace this request; check Ads Manager.");
      if (!alive.current || latestScope.current !== scope) return;
      const response = await apiRequest("POST", `/api/brands/${pending.brandId}/ads-manager/duplicate-ads/launch`, pending.payload);
      const result = await response.json() as { duplicationId?: string };
      if (!alive.current || latestScope.current !== scope) return;
      if (!result.duplicationId) throw new Error("Admission could not be confirmed. Do not start a replacement run.");
      if (!props.onAdmitted) setStarted({ scope, id: result.duplicationId });
      setOpenScope(null); onSelectionChange([]);
      clearPendingAdmission(admission.actorId, pending.payload.requestKey);
      props.onAdmitted?.(result.duplicationId);
    } catch (error) {
      if (alive.current && latestScope.current === scope) setRecoveryError(duplicationErrorMessage(error));
      // A recovery rejection cannot prove an earlier ambiguous write was never
      // admitted. Retain the original request until a run ID is confirmed.
    } finally {
      recoveryGuard.current = false;
      if (alive.current && latestScope.current === scope) setRecovering(false);
    }
  }
  if (!enabled && !props.recoveryOnly) return null;
  return <>
    {!props.recoveryOnly && visible && <span className="inline-flex flex-col gap-1">
      <Button size="sm" variant="outline" disabled={!!reason} onClick={() => setOpenScope(selectionScope)}>
        <Copy className="mr-1 h-3 w-3" />{props.buttonLabel ?? "Duplicate to new campaign"}
      </Button>
      {reason && <span role="status" className="text-xs">{reason}</span>}
    </span>}
    {started?.scope === scope && <Alert data-testid="duplication-started">
      <AlertTitle>Duplication started</AlertTitle>
      <AlertDescription>Run ID: {started.id}. Source ads remain unchanged. New objects are paused and nothing spends until enabled.</AlertDescription>
    </Alert>}
    {admission.pending && (props.recoveryOnly || !dialogVisible) && <Alert data-testid="duplication-unresolved">
      <AlertTitle>Prior duplication admission needs confirmation</AlertTitle>
      <AlertDescription>
        Request key: {admission.pending.payload.requestKey}. Do not start a replacement run.
        {admission.pending.brandId === brandId && admission.pending.payload.accountId === accountId
          ? <><Button size="sm" variant="outline" disabled={recovering || !ready || !enabled || capability.isFetching || capability.isError} onClick={confirmPriorAdmission}>{recovering ? "Confirming admission..." : "Confirm prior admission"}</Button>
            {capability.isFetching ? <p>Checking prior-admission recovery access...</p>
              : (capability.isError || !enabled) && <p role="alert">Prior-admission recovery is unavailable under current permissions or proof-engine settings. The saved request is retained; ask an administrator to check it.
                <Button variant="link" size="sm" onClick={() => void capability.refetch()}>Retry recovery access</Button></p>}</>
          : <p>Return to the original brand and account to confirm this admission. The saved request is retained.</p>}
        {recoveryError && <p role="alert">{recoveryError}</p>}
      </AlertDescription>
    </Alert>}
    {!props.recoveryOnly && dialogVisible && (
      brand.data ? <DuplicateAdsDialog key={selectionScope} brandId={brandId} brandName={brand.data.name}
        accountId={accountId} currency={currency} ads={ads} onClose={() => setOpenScope(null)}
        onRemoveBlocked={ids => {
          const remaining = selectedIds.filter(id => !ids.includes(id));
          setOpenScope(remaining.length ? JSON.stringify([scope, remaining]) : null);
          onSelectionChange(remaining);
        }}
        onAdmitted={id => {
          if (!props.onAdmitted) setStarted({ scope, id });
          setOpenScope(null);
          onSelectionChange([]);
          props.onAdmitted?.(id);
        }} />
        : <Alert><AlertDescription>{brand.isError ? "Brand name is unavailable. Close and try again." : "Loading duplication setup..."}</AlertDescription>
          <Button variant="outline" onClick={() => setOpenScope(null)}>Close</Button></Alert>
    )}
  </>;
}
